import { useState, useRef } from 'react';
import { truncatePath } from '../utils/paths';
import { STANDARD_EDITOR_LIMIT_BYTES } from '../../shared/editorLimits.js';

const LARGE_FILE_VIEW_CHUNK_BYTES = 256 * 1024;

const IMAGE_EXTENSIONS = new Set([
  '.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg', '.ico', '.bmp'
]);

const TEXT_EXTENSIONS = new Set([
  '.txt', '.log', '.md', '.markdown', '.rst',
  '.json', '.json5', '.jsonc',
  '.yml', '.yaml',
  '.toml', '.ini', '.cfg', '.conf', '.config', '.env',
  '.properties', '.prop',
  '.xml', '.html', '.htm', '.xhtml',
  '.css', '.scss', '.sass', '.less',
  '.js', '.mjs', '.cjs', '.jsx',
  '.ts', '.tsx',
  '.java', '.kt', '.kts', '.groovy',
  '.php', '.rb', '.py', '.go', '.rs', '.c', '.cpp', '.h', '.hpp', '.cs',
  '.lua', '.sh', '.bash', '.zsh', '.fish', '.bat', '.cmd', '.ps1',
  '.sql', '.graphql', '.gql',
  '.gitignore', '.gitattributes', '.editorconfig', '.htaccess',
  '.lock', '.list', '.csv', '.tsv',
]);

const getExtension = (path) => {
  if (!path) return '';
  const lower = path.toLowerCase();
  const dot = lower.lastIndexOf('.');
  return dot >= 0 ? lower.slice(dot) : '';
};

const isImageFile = (path) => IMAGE_EXTENSIONS.has(getExtension(path));
const isTextFile = (path) => {
  const ext = getExtension(path);
  if (!ext) return true; // no extension — allow (e.g. Dockerfile, Makefile)
  return TEXT_EXTENSIONS.has(ext);
};

export const useEditors = ({
  api,
  startTransfer,
  refreshLocal,
  refreshRemote,
  localPath,
  remotePath,
  connection,
  addLog,
  setMessage
}) => {
  const [editor, setEditor] = useState(null);
  const [nbtEditor, setNbtEditor] = useState(null);
  const [editorView, setEditorView] = useState('config');
  const [configEditor, setConfigEditor] = useState(null);
  const [loadingFilePath, setLoadingFilePath] = useState(null);
  const imageUrlRef = useRef(null);

  const nbtEnabled = api?.capabilities?.nbt !== false;

  const cleanupImageUrl = () => {
    if (imageUrlRef.current) {
      if (imageUrlRef.current.startsWith('blob:')) {
        URL.revokeObjectURL(imageUrlRef.current);
      }
      imageUrlRef.current = null;
    }
  };

  const loadLargeFileChunk = async (targetOffset, currentEditor = null) => {
    const target = currentEditor || editor;
    if (!target?.isLargeFile || !api?.readRemoteFileChunk) return;
    setLoadingFilePath(target.path);
    try {
      const totalSize = Number(target.totalSize) || 0;
      const chunkSize = Number(target.chunkSize) || LARGE_FILE_VIEW_CHUNK_BYTES;
      const safeOffset = totalSize > 0 ?
        Math.max(0, Math.min(Number(targetOffset) || 0, totalSize - 1)) :
        0;
      const chunk = await api.readRemoteFileChunk(target.path, safeOffset, chunkSize);
      cleanupImageUrl();
      setEditor((prev) => {
        if (!prev || prev.path !== target.path) return prev;
        return {
          ...prev,
          content: chunk.content || '',
          isLargeFile: true,
          readOnly: true,
          chunkOffset: chunk.offset || 0,
          chunkBytes: chunk.bytesRead || 0,
          chunkSize: chunk.requestedLength || chunkSize,
          totalSize: Number.isFinite(chunk.totalSize) ? chunk.totalSize : totalSize,
        };
      });
    } catch (err) {
      if (setMessage) setMessage(err.message);
    } finally {
      setLoadingFilePath(null);
    }
  };

  const openEditor = async (item, isRemote) => {
    if (item.type !== 'file') return;
    if (!api) {
      if (setMessage) setMessage('API bridge unavailable.');
      return;
    }
    const lowerName = (item.name || item.path || '').toLowerCase();

    if (nbtEnabled && (lowerName.endsWith('.dat') || lowerName.endsWith('.mca'))) {
      setLoadingFilePath(item.path);
      await openNbtEditor(item, isRemote);
      setLoadingFilePath(null);
      return;
    }

    if (!isTextFile(item.path) && !isImageFile(item.path)) {
      if (setMessage) setMessage(`Cannot open "${item.name}" — binary files are not supported in the editor.`);
      return;
    }

    const isLargeRemoteTextFile =
      isRemote &&
      !isImageFile(item.path) &&
      Number.isFinite(item?.size) &&
      item.size > STANDARD_EDITOR_LIMIT_BYTES;

    if (isLargeRemoteTextFile) {
      if (connection?.protocol !== 'sftp' || typeof api.readRemoteFileChunk !== 'function') {
        if (setMessage) setMessage('Large-file preview is currently available for SFTP connections only.');
        return;
      }

      setLoadingFilePath(item.path);
      const tracker = startTransfer(`Open large file: ${truncatePath(item.path, 30)}`, item.path);
      try {
        cleanupImageUrl();
        const chunk = await api.readRemoteFileChunk(item.path, 0, LARGE_FILE_VIEW_CHUNK_BYTES);
        setEditor({
          path: item.path,
          isRemote,
          content: chunk.content || '',
          isLargeFile: true,
          readOnly: true,
          totalSize: Number.isFinite(chunk.totalSize) ? chunk.totalSize : item.size,
          chunkOffset: chunk.offset || 0,
          chunkBytes: chunk.bytesRead || 0,
          chunkSize: chunk.requestedLength || LARGE_FILE_VIEW_CHUNK_BYTES
        });
        setEditorView('raw');
        if (addLog) addLog(`Opened large file preview: ${truncatePath(item.path, 30)}`);
        tracker.succeed();
      } catch (err) {
        tracker.fail(err.message);
        if (setMessage) setMessage(err.message);
      } finally {
        setLoadingFilePath(null);
      }
      return;
    }

    if (isRemote && isImageFile(item.path) && api.readRemoteFileAsDataUrl) {
      setLoadingFilePath(item.path);
      const tracker = startTransfer(`Open image: ${truncatePath(item.path, 30)}`, item.path);
      try {
        cleanupImageUrl();
        const url = await api.readRemoteFileAsDataUrl(item.path);
        imageUrlRef.current = url;
        setEditor({ path: item.path, isRemote, content: '', isImage: true, imageUrl: url });
        setEditorView('raw');
        if (addLog) addLog(`Opened image: ${truncatePath(item.path, 30)}`);
        tracker.succeed();
      } catch (err) {
        tracker.fail(err.message);
        if (setMessage) setMessage(err.message);
      } finally {
        setLoadingFilePath(null);
      }
      return;
    }

    setLoadingFilePath(item.path);
    const tracker = startTransfer(`Open: ${truncatePath(item.path, 30)}`, item.path);
    try {
      const content = isRemote ?
      await api.readRemoteFile(item.path) :
      await api.readLocalFile(item.path);

      cleanupImageUrl();
      setEditor({ path: item.path, isRemote, content });

      if (lowerName.endsWith('.json') || lowerName.endsWith('.yml') || lowerName.endsWith('.yaml') || lowerName.endsWith('.properties') || lowerName.endsWith('.toml') || lowerName.endsWith('.xml')) {

        setEditorView('config');
      } else {
        setEditorView('raw');
      }

      if (addLog) addLog(`Opened ${isRemote ? 'remote' : 'local'}: ${truncatePath(item.path, 30)}`);
      tracker.succeed();
    } catch (err) {
      tracker.fail(err.message);
      if (setMessage) setMessage(err.message);
    } finally {
      setLoadingFilePath(null);
    }
  };

  const saveEditor = async () => {
    if (!editor || !api) return;
    if (editor.isLargeFile) {
      if (setMessage) setMessage('Large file preview is read-only.');
      return;
    }
    const tracker = startTransfer('Save file', editor.path, 'action');
    try {
      if (editor.isRemote) {
        await api.writeRemoteFile(editor.path, editor.content);
        await refreshRemote(remotePath, { silentLog: true });
        if (addLog) addLog(`Saved remote: ${truncatePath(editor.path, 30)}`);
      } else {
        await api.writeLocalFile(editor.path, editor.content);
        await refreshLocal(localPath);
        if (addLog) addLog(`Saved local: ${truncatePath(editor.path, 30)}`);
      }
      tracker.succeed();
    } catch (err) {
      tracker.fail(err.message);
      if (setMessage) setMessage(err.message);
    }
  };

  const openNbtEditor = async (item, isRemote) => {
    if (!api || !nbtEnabled) {
      if (setMessage) setMessage('NBT tools are disabled in web mode.');
      return;
    }
    const tracker = startTransfer('Open NBT', item.path);
    try {









      const lower = item.path.toLowerCase();
      if (lower.endsWith('.mca')) {
        const headers = await api.readRegionHeaders(item.path);
        setNbtEditor({
          path: item.path,
          isRemote,
          mode: 'region',
          chunks: headers,
          selectedChunkIndex: null,
          text: '',
          dirty: false,
          loadingChunk: false
        });
      } else {
        const data = await api.readNbt(item.path);
        setNbtEditor({
          path: item.path,
          isRemote,
          mode: 'file',
          text: JSON.stringify(data, null, 2),
          dirty: false,
          loadingChunk: false
        });
      }
      tracker.succeed();
    } catch (err) {
      tracker.fail(err.message);
      if (setMessage) setMessage(err.message);
    }
  };

  const saveNbtEditor = async () => {
    if (!nbtEditor || !api) return;
    try {
      if (nbtEditor.mode === 'file') {
        const parsed = JSON.parse(nbtEditor.text);
        await api.writeNbt(nbtEditor.path, parsed);
      } else {
        const selectedChunk =
          Number.isInteger(nbtEditor.selectedChunkIndex) ?
            nbtEditor.chunks?.[nbtEditor.selectedChunkIndex] :
            null;
        if (selectedChunk) {
          const parsed = JSON.parse(nbtEditor.text);
          await api.writeRegionChunk(nbtEditor.path, selectedChunk.x, selectedChunk.z, parsed);
        }
      }
      if (addLog) addLog(`Saved NBT ${nbtEditor.path}`);
      setNbtEditor((prev) => prev ? { ...prev, dirty: false } : prev);
    } catch (err) {
      if (setMessage) setMessage(err.message);
    }
  };


  const loadNbtChunk = async (index) => {
    if (!nbtEditor || nbtEditor.mode !== 'region' || !api) return;
    const chunk = nbtEditor.chunks[index];
    if (!chunk) return;
    try {
      setNbtEditor((prev) => prev ? { ...prev, loadingChunk: true } : prev);
      const data = await api.readRegionChunk(nbtEditor.path, chunk.x, chunk.z);
      setNbtEditor((prev) => ({
        ...(prev || {}),
        selectedChunkIndex: index,
        text: JSON.stringify(data, null, 2),
        dirty: false,
        loadingChunk: false
      }));
    } catch (err) {
      setNbtEditor((prev) => prev ? { ...prev, loadingChunk: false } : prev);
      if (setMessage) setMessage(err.message);
    }
  };

  return {
    editor,
    setEditor,
    nbtEditor,
    setNbtEditor,
    editorView,
    setEditorView,
    configEditor,
    setConfigEditor,
    loadingFilePath,
    openEditor,
    loadLargeFileChunk,
    saveEditor,
    openNbtEditor,
    saveNbtEditor,
    loadNbtChunk
  };
};
